import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { RecordingSummary } from "@rec/shared";
import { api } from "@/lib/api";
import { MediaCard } from "@/components/MediaCard";
import { Button, EmptyState, ErrorState, Skeleton } from "@/components/ui";

/**
 * The library, as a modal picker for Studio.
 *
 * Same cards, same search, same folder filter as the Library page, so
 * "importing from the library" *is* the library — not a second, worse list.
 * Picking never closes the dialog: assembling a timeline means adding several
 * videos in a row, and reopening a modal per clip punishes exactly the person
 * using the feature most. Added ones show a check and can be added again
 * (a duplicate source is legitimate — that is what duplicated tracks are).
 */
export function LibraryPicker({
  addedIds,
  onAdd,
  onClose,
}: {
  /** Recordings already on the timeline, marked so the picker shows state. */
  addedIds: Set<string>;
  onAdd: (recording: RecordingSummary) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation(["library", "common"]);
  const [search, setSearch] = useState("");
  const [folder, setFolder] = useState("");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const recordings = useQuery({
    queryKey: ["recordings"],
    queryFn: api.listRecordings,
    refetchInterval: (query) =>
      query.state.data?.some((r) => r.status === "processing") ? 5000 : false,
  });
  const folders = useQuery({ queryKey: ["projects"], queryFn: api.listProjects });

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    return (recordings.data ?? [])
      .filter((r) => r.status === "ready" || r.status === "processing")
      .filter((r) =>
        folder === "" ? true : folder === "none" ? r.projectId === null : r.projectId === folder,
      )
      .filter((r) => (query ? r.title.toLowerCase().includes(query) : true));
  }, [recordings.data, search, folder]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t("picker.title")}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-xl border border-border bg-surface shadow-[var(--shadow-md)]">
        <div className="flex items-center justify-between gap-4 border-b border-border p-4">
          <div>
            <h2 className="text-[15px] font-semibold text-text">{t("picker.title")}</h2>
            <p className="mt-0.5 text-[12.5px] text-text-muted">
              {t("picker.hint")}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-text-faint hover:bg-surface-hover hover:text-text"
            aria-label={t("common:actions.close")}
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
          <div className="relative min-w-0 flex-1 basis-52">
            <Search
              size={13}
              className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-text-faint"
            />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("toolbar.searchLibrary")}
              autoFocus
              className="w-full rounded-lg border border-border bg-bg-subtle py-1.5 pr-2.5 pl-8 text-[13px] text-text placeholder:text-text-faint focus:border-accent focus:outline-none"
            />
          </div>
          {(folders.data ?? []).length > 0 ? (
            <select
              value={folder}
              onChange={(e) => setFolder(e.target.value)}
              className="rounded-lg border border-border bg-bg-subtle px-2 py-1.5 text-[12.5px] text-text focus:border-accent focus:outline-none"
              title={t("picker.folderFilter")}
            >
              <option value="">{t("picker.allFolders")}</option>
              <option value="none">{t("picker.notInFolder")}</option>
              {(folders.data ?? []).map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          ) : null}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          {recordings.isLoading ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {Array.from({ length: 6 }, (_, i) => (
                <Skeleton key={i} className="aspect-[16/12] rounded-xl" />
              ))}
            </div>
          ) : recordings.isError ? (
            <ErrorState
              title={t("empty.loadLibraryFailed")}
              error={t("picker.loadFailedHint")}
            />
          ) : visible.length === 0 ? (
            <EmptyState
              title={search || folder ? t("empty.nothingMatches") : t("empty.noVideos")}
              description={
                search || folder
                  ? t("empty.clearOrPick")
                  : t("picker.emptyHint")
              }
            />
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {visible.map((recording) => {
                const ready = recording.status === "ready";
                const added = addedIds.has(recording.id);
                return (
                  <MediaCard
                    key={recording.id}
                    recording={recording}
                    onOpen={ready ? () => onAdd(recording) : undefined}
                    action={
                      ready ? (
                        <Button
                          variant={added ? "ghost" : "secondary"}
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            onAdd(recording);
                          }}
                          title={
                            added
                              ? t("picker.alreadyAdded")
                              : t("picker.addToEnd")
                          }
                        >
                          {added ? <Check size={12} /> : null}
                          {added ? t("picker.added") : t("common:actions.add")}
                        </Button>
                      ) : undefined
                    }
                  />
                );
              })}
            </div>
          )}
        </div>

        <div className="flex justify-end border-t border-border p-3">
          <Button variant="secondary" size="sm" onClick={onClose}>
            {t("common:actions.done")}
          </Button>
        </div>
      </div>
    </div>
  );
}
